/**
 * Web counterpart to imagePipeline.ts — same public contract
 * (`processImageForUpload(uri, purpose): Promise<string>`), backed by the
 * Canvas API instead of @bam.tech/react-native-image-resizer (native-only,
 * not imported here at all so it's never pulled into the web bundle).
 *
 * Re-encoding through <canvas> strips EXIF/IPTC/XMP the same way the native
 * pipeline's re-encode does (the canvas only ever sees decoded pixels, no
 * metadata segments survive `toBlob`), and resizing never upscales, mirroring
 * `onlyScaleDown: true` on the native side.
 */
export type ImageUploadPurpose = 'profile' | 'cover' | 'chat' | 'verification';

type Preset = { maxDimension: number; quality: number };

const IMAGE_PRESETS: Record<ImageUploadPurpose, Preset> = {
  profile: { maxDimension: 1080, quality: 0.85 },
  cover: { maxDimension: 1080, quality: 0.85 },
  chat: { maxDimension: 1600, quality: 0.88 },
  verification: { maxDimension: 1600, quality: 0.92 },
};

function loadImage(uri: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error(`Failed to load image at ${uri}`));
    img.src = uri;
  });
}

export async function processImageForUpload(
  uri: string,
  purpose: ImageUploadPurpose,
): Promise<string> {
  const preset = IMAGE_PRESETS[purpose];
  const img = await loadImage(uri);

  const scale = Math.min(1, preset.maxDimension / Math.max(img.naturalWidth, img.naturalHeight));
  const width = Math.max(1, Math.round(img.naturalWidth * scale));
  const height = Math.max(1, Math.round(img.naturalHeight * scale));

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas 2D context unavailable');
  ctx.drawImage(img, 0, 0, width, height);

  const blob = await new Promise<Blob | null>(resolve => {
    canvas.toBlob(resolve, 'image/jpeg', preset.quality);
  });
  if (!blob) throw new Error('Failed to encode image for upload');

  return URL.createObjectURL(blob);
}
